import React, { useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import {
  useGetComponentPoliciesQuery,
  useCreateComponentPolicyMutation,
  useUpdateComponentPolicyMutation,
  useDeleteComponentPolicyMutation,
} from '../../redux/api/portalApi';
import {
  COMPONENT_REGISTRY,
  PERMISSION_LEVELS,
  ROLE_HIERARCHY,
  ROLE_LABELS,
  getAllSections,
} from '../../config/componentRegistry';
import { usePermissions } from '../../hooks/usePermissions';

const ENVIRONMENT_OPTIONS = [
  { value: 'dev', label: 'Development' },
  { value: 'uat', label: 'UAT' },
  { value: 'production', label: 'Production' },
];

const DEFAULT_ENVIRONMENTS = ENVIRONMENT_OPTIONS.map((option) => option.value);
const STATUS_OPTIONS = ['draft', 'published'];
const ROLE_PERMISSION_INHERIT = '';
const DEFAULT_PERMISSION_INHERIT = '';
const getRoleLabel = (roleName) => ROLE_LABELS[roleName] || roleName;

const normalizePermission = (value, fallback = PERMISSION_LEVELS.HIDDEN) => {
  const normalized = String(value || '').toLowerCase();
  if (normalized === PERMISSION_LEVELS.EDIT) return PERMISSION_LEVELS.EDIT;
  if (normalized === PERMISSION_LEVELS.VIEW) return PERMISSION_LEVELS.VIEW;
  if (normalized === PERMISSION_LEVELS.HIDDEN) return PERMISSION_LEVELS.HIDDEN;
  return fallback;
};

const normalizeEnvironment = (value) => {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'development') return 'dev';
  if (normalized === 'dev' || normalized === 'uat' || normalized === 'production') {
    return normalized;
  }
  return null;
};

const normalizeEnvironmentArray = (input, fallback = DEFAULT_ENVIRONMENTS) => {
  if (Array.isArray(input)) {
    const normalizedValues = input
      .map((value) => normalizeEnvironment(value))
      .filter(Boolean);

    return normalizedValues.length > 0
      ? Array.from(new Set(normalizedValues))
      : [...fallback];
  }

  if (typeof input === 'string' && input.trim()) {
    const trimmed = input.trim();

    try {
      const parsed = JSON.parse(trimmed);
      return normalizeEnvironmentArray(parsed, fallback);
    } catch (_) {
      const values = trimmed
        .split(',')
        .map((value) => normalizeEnvironment(value))
        .filter(Boolean);

      return values.length > 0
        ? Array.from(new Set(values))
        : [...fallback];
    }
  }

  const normalizedSingleValue = normalizeEnvironment(input);
  if (normalizedSingleValue) {
    return [normalizedSingleValue];
  }

  return [...fallback];
};

const toApiEnvironmentValue = (environments) =>
  JSON.stringify(normalizeEnvironmentArray(environments));

const toBoolean = (value, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (value.toLowerCase() === 'true') return true;
    if (value.toLowerCase() === 'false') return false;
  }
  if (typeof value === 'number') return value === 1;
  return fallback;
};

const parseRolePermissions = (input) => {
  if (Array.isArray(input)) {
    return input
      .map((entry) => {
        const roleName = entry?.roleName || entry?.RoleName || '';
        const permission = normalizePermission(
          entry?.permission ?? entry?.Permission,
          null
        );

        return {
          roleName,
          permission,
        };
      })
      .filter((entry) => entry.roleName && entry.permission !== null);
  }

  if (typeof input === 'string' && input.trim()) {
    try {
      return parseRolePermissions(JSON.parse(input));
    } catch (_) {
      return [];
    }
  }

  return [];
};

const normalizeRolePermission = (value) => {
  if (value === ROLE_PERMISSION_INHERIT) {
    return ROLE_PERMISSION_INHERIT;
  }

  return normalizePermission(value);
};

const normalizeDefaultPermission = (value) =>
  normalizePermission(value, DEFAULT_PERMISSION_INHERIT);

const toApiRolePermissions = (rolePermissions = []) => (
  rolePermissions
    .filter((entry) => entry?.roleName && entry.permission !== ROLE_PERMISSION_INHERIT)
    .map((entry) => ({
      RoleName: entry.roleName,
      Permission: normalizePermission(entry.permission),
    }))
);

const createEmptyForm = (roleOptions) => ({
  id: null,
  policyId: '',
  componentKey: '',
  environments: [...DEFAULT_ENVIRONMENTS],
  enabled: true,
  requiresAuth: false,
  defaultPermission: DEFAULT_PERMISSION_INHERIT,
  status: 'draft',
  version: 1,
  rolePermissions: roleOptions.map((roleName) => ({
    roleName,
    permission: ROLE_PERMISSION_INHERIT,
  })),
});

const generatePolicyId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `policy-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const mapPolicyToForm = (policy, roleOptions) => {
  const rolePermissionMap = new Map(
    parseRolePermissions(policy?.rolePermissions ?? policy?.RolePermissions).map((entry) => [
      entry.roleName,
      normalizePermission(entry.permission),
    ])
  );

  const environments = normalizeEnvironmentArray(
    policy?.environments ??
    policy?.Environments ??
    policy?.environment ??
    policy?.Environment
  );

  return {
    id: policy?.id ?? policy?.ID ?? null,
    policyId: policy?.policyId || policy?.PolicyId || '',
    componentKey: policy?.componentKey || policy?.ComponentKey || '',
    environments,
    enabled: toBoolean(policy?.enabled ?? policy?.Enabled, true),
    requiresAuth: toBoolean(policy?.requiresAuth ?? policy?.RequiresAuth, false),
    defaultPermission: normalizeDefaultPermission(
      policy?.defaultPermission ?? policy?.DefaultPermission
    ),
    status: String(policy?.status || policy?.Status || 'draft').toLowerCase(),
    version: Number(policy?.version ?? policy?.Version ?? 1),
    rolePermissions: roleOptions.map((roleName) => ({
      roleName,
      permission: rolePermissionMap.get(roleName) ?? ROLE_PERMISSION_INHERIT,
    })),
  };
};

const AdminComponentPolicies = () => {
  const { canView, canEdit, isLoading: isPermissionsLoading } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: '', message: '' });
  const [formData, setFormData] = useState(null);
  const canViewComponentPolicies = canView('admin.component-policies');
  const canEditComponentPolicies = canEdit('admin.component-policies');

  const roleOptions = Object.keys(ROLE_HIERARCHY)
    .sort((left, right) => ROLE_HIERARCHY[right] - ROLE_HIERARCHY[left]);

  const sectionNames = useMemo(() => getAllSections().sort((a, b) => a.localeCompare(b)), []);

  const {
    data: policies = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetComponentPoliciesQuery();

  const [createComponentPolicy, { isLoading: isCreating }] = useCreateComponentPolicyMutation();
  const [updateComponentPolicy, { isLoading: isUpdating }] = useUpdateComponentPolicyMutation();
  const [deleteComponentPolicy, { isLoading: isDeleting }] = useDeleteComponentPolicyMutation();

  const isSaving = isCreating || isUpdating;

  const usedComponentKeys = useMemo(
    () =>
      new Set(
        (Array.isArray(policies) ? policies : [])
          .map((policy) => policy?.componentKey || policy?.ComponentKey || '')
          .filter(Boolean)
      ),
    [policies]
  );

  const filteredPolicies = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    return (Array.isArray(policies) ? policies : []).filter((policy) => {
      const componentKey = String(policy?.componentKey || policy?.ComponentKey || '').toLowerCase();

      if (!normalizedSearchTerm) {
        return true;
      }

      return componentKey.includes(normalizedSearchTerm);
    });
  }, [policies, searchTerm]);

  const openCreateModal = () => {
    if (!canEditComponentPolicies) {
      return;
    }

    setFormData(createEmptyForm(roleOptions));
    setStatusMessage({ type: '', message: '' });
    setShowModal(true);
  };

  const openEditModal = (policy) => {
    if (!canEditComponentPolicies) {
      return;
    }

    setFormData(mapPolicyToForm(policy, roleOptions));
    setStatusMessage({ type: '', message: '' });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData(null);
  };

  const handleRolePermissionChange = (roleName, permission) => {
    setFormData((previous) => ({
      ...previous,
      rolePermissions: previous.rolePermissions.map((entry) =>
        entry.roleName === roleName
          ? { ...entry, permission: normalizeRolePermission(permission) }
          : entry
      ),
    }));
  };

  const handleEnvironmentToggle = (environment, checked) => {
    setFormData((previous) => {
      const currentEnvironments = normalizeEnvironmentArray(previous?.environments);
      const nextEnvironments = checked
        ? Array.from(new Set([...currentEnvironments, environment]))
        : currentEnvironments.filter((value) => value !== environment);

      return {
        ...previous,
        environments: nextEnvironments,
      };
    });
  };

  const handleSave = async () => {
    if (!canEditComponentPolicies) {
      return;
    }

    if (!formData?.componentKey) {
      setStatusMessage({ type: 'danger', message: 'Component key is required.' });
      return;
    }

    const environments = normalizeEnvironmentArray(formData?.environments, []);
    if (environments.length === 0) {
      setStatusMessage({ type: 'danger', message: 'Select at least one environment.' });
      return;
    }

    const payload = {
      PolicyId: formData.policyId || generatePolicyId(),
      ComponentKey: formData.componentKey,
      Environment: toApiEnvironmentValue(environments),
      Enabled: Boolean(formData.enabled),
      RequiresAuth: Boolean(formData.requiresAuth),
      RolePermissions: toApiRolePermissions(formData.rolePermissions),
      Status: formData.status,
      Version: Number(formData.version || 1),
    };

    if (formData.defaultPermission !== DEFAULT_PERMISSION_INHERIT) {
      payload.DefaultPermission = normalizePermission(formData.defaultPermission);
    }

    try {
      if (formData.id !== null && formData.id !== undefined) {
        await updateComponentPolicy({
          id: formData.id,
          ...payload,
        }).unwrap();
        setStatusMessage({ type: 'success', message: 'Component policy updated successfully.' });
      } else {
        await createComponentPolicy(payload).unwrap();
        setStatusMessage({ type: 'success', message: 'Component policy created successfully.' });
      }

      closeModal();
    } catch (saveError) {
      const saveMessage = saveError?.message || 'Failed to save component policy.';
      setStatusMessage({ type: 'danger', message: saveMessage });
    }
  };

  const handleDelete = async (id) => {
    if (!canEditComponentPolicies || !id) {
      return;
    }

    if (!window.confirm('Delete this component policy?')) {
      return;
    }

    try {
      await deleteComponentPolicy(id).unwrap();
      setStatusMessage({ type: 'success', message: 'Component policy deleted successfully.' });
    } catch (deleteError) {
      const deleteMessage = deleteError?.message || 'Failed to delete component policy.';
      setStatusMessage({ type: 'danger', message: deleteMessage });
    }
  };

  if (isPermissionsLoading) {
    return (
      <div className="d-flex justify-content-center py-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!canViewComponentPolicies) {
    return <Alert variant="danger">Access denied. You do not have permission to view component policies.</Alert>;
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <h2 className="mb-4">Component Policy Manager</h2>

          {statusMessage.message && (
            <Alert
              variant={statusMessage.type || 'info'}
              onClose={() => setStatusMessage({ type: '', message: '' })}
              dismissible
            >
              {statusMessage.message}
            </Alert>
          )}

          {!canEditComponentPolicies && (
            <Alert variant="info">
              Component policies are in read-only mode based on your current permissions.
            </Alert>
          )}

          <div className="card mb-4">
            <div className="card-body">
              <Row className="g-3 align-items-end">
                <Col md={5}>
                  <Form.Group controlId="componentPolicySearch">
                    <Form.Label>Search Component Key</Form.Label>
                    <Form.Control
                      type="text"
                      value={searchTerm}
                      placeholder="home.apps-portal"
                      onChange={(event) => setSearchTerm(event.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <div className="text-muted small mt-2">
                    <em>Single policy can target multiple environments</em>
                  </div>
                </Col>
                <Col md={4} className="d-flex justify-content-end gap-2">
                  <Button variant="outline-secondary" onClick={() => refetch()} disabled={isFetching}>
                    Refresh
                  </Button>
                  <Button variant="primary" onClick={openCreateModal} disabled={!canEditComponentPolicies}>
                    New Policy
                  </Button>
                </Col>
              </Row>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              {isLoading ? (
                <div className="d-flex justify-content-center py-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : error ? (
                <Alert variant="danger">Failed to load component policies.</Alert>
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Component Key</th>
                      <th>Environments</th>
                      <th>Enabled</th>
                      <th>Default Permission</th>
                      <th>Status</th>
                      <th>Version</th>
                      <th>Role Rules</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPolicies.map((policy) => {
                      const id = policy?.id ?? policy?.ID;
                      const componentKey = policy?.componentKey || policy?.ComponentKey || '';
                      const environments = normalizeEnvironmentArray(
                        policy?.environments ??
                        policy?.Environments ??
                        policy?.environment ??
                        policy?.Environment
                      );
                      const rolePermissions = parseRolePermissions(
                        policy?.rolePermissions ?? policy?.RolePermissions
                      );

                      return (
                        <tr key={id || componentKey}>
                          <td>{componentKey}</td>
                          <td>{environments.join(', ')}</td>
                          <td>{toBoolean(policy?.enabled ?? policy?.Enabled, true) ? 'Yes' : 'No'}</td>
                          <td>
                            {normalizeDefaultPermission(
                              policy?.defaultPermission ?? policy?.DefaultPermission
                            ) || 'inherit'}
                          </td>
                          <td>{String(policy?.status || policy?.Status || 'draft')}</td>
                          <td>{Number(policy?.version ?? policy?.Version ?? 0)}</td>
                          <td>
                            {
                              rolePermissions.filter(
                                (entry) => entry.permission !== PERMISSION_LEVELS.HIDDEN
                              ).length
                            }
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => openEditModal(policy)}
                                disabled={!canEditComponentPolicies}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => handleDelete(id)}
                                disabled={isDeleting || !canEditComponentPolicies}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredPolicies.length === 0 && (
                      <tr>
                        <td colSpan={8} className="text-center">
                          No component policies found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={closeModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{formData?.id ? 'Edit Component Policy' : 'Create Component Policy'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formData && (
            <Form>
              <Row className="g-3 mb-3">
                <Col md={6}>
                  <Form.Group controlId="componentPolicyComponentKey">
                    <Form.Label>Component Key</Form.Label>
                    <Form.Select
                      value={formData.componentKey}
                      onChange={(event) =>
                        setFormData((previous) => ({
                          ...previous,
                          componentKey: event.target.value,
                        }))
                      }
                    >
                      <option value="">Select component</option>
                      {sectionNames.map((section) => {
                        const sectionItems = Object.entries(COMPONENT_REGISTRY)
                          .filter(([key, config]) => {
                            if (config.section !== section) return false;
                            // Always show the key being edited; hide keys that already have a policy.
                            return key === formData.componentKey || !usedComponentKeys.has(key);
                          })
                          .sort(([left], [right]) => left.localeCompare(right));

                        if (sectionItems.length === 0) return null;

                        return (
                          <optgroup key={section} label={`${section} section`}>
                            {sectionItems.map(([componentKey]) => (
                              <option key={componentKey} value={componentKey}>
                                {componentKey}
                              </option>
                            ))}
                          </optgroup>
                        );
                      })}
                    </Form.Select>
                    {COMPONENT_REGISTRY[formData.componentKey]?.description && (
                      <Form.Text className="text-muted">
                        {COMPONENT_REGISTRY[formData.componentKey].description}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="componentPolicyEnvironments">
                    <Form.Label>Environments</Form.Label>
                    <div className="border rounded p-2">
                      {ENVIRONMENT_OPTIONS.map((option) => (
                        <Form.Check
                          key={option.value}
                          type="checkbox"
                          id={`component-policy-env-${option.value}`}
                          label={option.label}
                          checked={normalizeEnvironmentArray(formData?.environments).includes(option.value)}
                          onChange={(event) => handleEnvironmentToggle(option.value, event.target.checked)}
                        />
                      ))}
                    </div>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="componentPolicyVersion">
                    <Form.Label>Version</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      value={formData.version}
                      onChange={(event) =>
                        setFormData((previous) => ({
                          ...previous,
                          version: Number(event.target.value || 1),
                        }))
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-3 mb-3">
                <Col md={4}>
                  <Form.Group controlId="componentPolicyDefaultPermission">
                    <Form.Label>Default Permission</Form.Label>
                    <Form.Select
                      value={formData.defaultPermission}
                      onChange={(event) =>
                        setFormData((previous) => ({
                          ...previous,
                          defaultPermission: normalizeDefaultPermission(event.target.value),
                        }))
                      }
                    >
                      <option value={DEFAULT_PERMISSION_INHERIT}>Use Registry Defaults</option>
                      <option value={PERMISSION_LEVELS.HIDDEN}>Hidden</option>
                      <option value={PERMISSION_LEVELS.VIEW}>View</option>
                      <option value={PERMISSION_LEVELS.EDIT}>Edit</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="componentPolicyStatus">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={formData.status}
                      onChange={(event) =>
                        setFormData((previous) => ({
                          ...previous,
                          status: event.target.value,
                        }))
                      }
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4} className="d-flex align-items-end gap-4">
                  <Form.Check
                    type="checkbox"
                    label="Enabled"
                    checked={formData.enabled}
                    onChange={(event) =>
                      setFormData((previous) => ({
                        ...previous,
                        enabled: event.target.checked,
                      }))
                    }
                  />
                  <Form.Check
                    type="checkbox"
                    label="Requires Auth"
                    checked={formData.requiresAuth}
                    onChange={(event) =>
                      setFormData((previous) => ({
                        ...previous,
                        requiresAuth: event.target.checked,
                      }))
                    }
                  />
                </Col>
              </Row>

              <h5 className="mt-4">Role Permissions</h5>
              <Table size="sm" bordered responsive>
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Permission</th>
                  </tr>
                </thead>
                <tbody>
                  {formData.rolePermissions.map((entry) => (
                    <tr key={entry.roleName}>
                      <td>{getRoleLabel(entry.roleName)}</td>
                      <td>
                        <Form.Select
                          size="sm"
                          value={entry.permission}
                          onChange={(event) =>
                            handleRolePermissionChange(entry.roleName, event.target.value)
                          }
                        >
                          <option value={ROLE_PERMISSION_INHERIT}>Use Default</option>
                          <option value={PERMISSION_LEVELS.HIDDEN}>Hidden</option>
                          <option value={PERMISSION_LEVELS.VIEW}>View</option>
                          <option value={PERMISSION_LEVELS.EDIT}>Edit</option>
                        </Form.Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSaving || !formData?.componentKey || !canEditComponentPolicies}
          >
            {isSaving ? 'Saving...' : 'Save Policy'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminComponentPolicies;
